export {
  ConsoleDataError,
  isNotPublished,
  notPublishedReason,
} from "./console/data-source.js";
export { companiesForPicker } from "./console/fixtures.js";

// Admin-only Books API client. Browser production traffic is same-origin
// `/v1/admin/*` (Pages Function attaches Access JWT + ADMIN_TOKEN) plus
// `/link/session` and `/link/exchange` (Access cookie only — invitation in
// the body, never ADMIN_TOKEN). Tests may still pass a bearer + apiBase to
// a stub. Client/report/hub surfaces are unreachable from here — enforced
// by test/fetch-guard.test.ts.

import { ADMIN_FETCH_CREDENTIALS, adminRequestHeaders } from "./admin_fetch.js";
import { isAdminApiPath, isLinkApiPath } from "./admin_path.js";
import { ReadCache, type ReadCacheOptions } from "./read-cache.js";
import {
  ConsoleDataError,
  createHttpConsoleDataSource,
  type ConsoleDataSource,
} from "./console/data-source.js";
import type {
  ActivityPage,
  ActivityQuery,
  ActivitySearchQuery,
  ActivitySearchResponse,
  AdminMonthsResponse,
  BalanceHistory,
  BalancesSnapshot,
  ConsoleTransactionsPage,
  GeneralLedgerPage,
  GeneralLedgerQuery,
  LedgerAccountsResponse,
  LedgerEntriesPage,
  LedgerQuery,
  LedgerSearchQuery,
  LedgerSearchResponse,
  LedgerStatement,
  LedgerStatementReport,
  ReportDataMap,
  ReportId,
  ReportSnapshot,
  ReportWindowCatalog,
  ReportWindowDownload,
  ReportWindowRequestResult,
  TransactionsQuery,
} from "./console/types.js";
import type {
  AdminHubIntakeResponse,
  AdminHubQuestionsResponse,
  AdminMe,
  AgentIdentityCreate,
  AgentIdentityCreateResponse,
  AgentIdentityList,
  AgentIdentityRevokeRequest,
  AgentIdentityRevokeResponse,
  CardRuleAsk,
  CheckNowStatus,
  Company,
  CompanyCreate,
  CompanyDraft,
  CompanyHealth,
  CompanySlug,
  CompanyUpdate,
  ChartSnapshot,
  DashboardGroup,
  IcSplitCard,
  IcSplitCardsResponse,
  DashboardGroupCreate,
  DashboardGroupUpdate,
  HubIntakeQuery,
  HubQuestionAssignResponse,
  LinkExchangeResponse,
  LinkInvitationIssueResponse,
  LinkSessionResponse,
  OpeningDocumentUploadResponse,
  OpeningDocumentUploadUrlRequest,
  OpeningDocumentUploadUrlResponse,
  OpeningDocumentExtractResponse,
  OpeningDocumentKind,
  OpeningDeterminationPut,
  OpeningDeterminationRecord,
  OpeningDeterminationsResponse,
  OpeningDocumentOcrResponse,
  OpeningOcrDeterminationField,
  OnboardingApprovalApproveResponse,
  OnboardingApprovalReadResponse,
  OnboardingDraftDeleteResponse,
  OnboardingRevealResponse,
  OnboardingRunCreate,
  OnboardingRunResponse,
  OnboardingStatus,
  OnboardingStepKey,
  ExpenseAuditBillLine,
  ExpenseAuditDecisionAccepted,
  ExpenseAuditDecisionBody,
  ExpenseAuditDecisionView,
  ExpenseAuditDocumentRef,
  ExpenseAuditItem,
  ExpenseAuditItemsPage,
  ExpenseAuditLeg,
  ExpenseAuditPairState,
  ExpenseAuditReceiptFacts,
  ExpenseAuditReviewState,
  ExpenseAuditSignedUrl,
  ExpenseAuditSource,
  ExpenseAuditViewRecord,
  PriorPaymentCandidatesResponse,
  IntercompanySnapshot,
  QueueItem,
  QueueResolution,
  QueueEscalateToPracticeRequest,
  QueueEscalateToPracticeResponse,
  ReceiptMatchCorrectionPage,
  Rule,
  RuleChange,
  RuleChangeRequest,
  RuleChangeRequestStatusFilter,
  LedgerChangePropose,
  LedgerChangeRequest,
  LedgerChangeTarget,
  StatementConfirmRequest,
  StatementConfirmResponse,
  StatementReconRow,
  StatementUploadResponse,
  TransactionAttachmentListResponse,
  TransactionAttachmentSource,
  TransactionAttachmentUploadResponse,
  TransactionAttachmentUrlResponse,
  TokenInfo,
  TokenIssueRequest,
  VerifyOpeningDocumentQuoteRequest,
  VerifyOpeningDocumentQuoteResponse,
  TokenIssueResponse,
  ReceiptShelfArchive,
  ReceiptShelfCandidate,
  ReceiptShelfItem,
  ReceiptShelfWarning,
  RecurringAdminGet,
  RecurringRunRequestResult,
  VendorIdentitySnapshotGet,
} from "./types.js";

export class AdminApiError extends Error {
  readonly requestId: string | null;

  constructor(
    readonly status: number,
    readonly body: unknown,
    requestIdHeader?: string | null,
  ) {
    super(`Admin API request failed (${String(status)})`);
    this.name = "AdminApiError";
    this.requestId = readAdminRequestId(body, requestIdHeader ?? null);
  }
}

/** Body `error.requestId` when present, otherwise the X-Request-ID header. */
export function readAdminRequestId(body: unknown, header: string | null): string | null {
  if (body !== null && typeof body === "object" && "error" in body) {
    const err = (body as { error?: unknown }).error;
    if (err !== null && typeof err === "object" && "requestId" in err) {
      const id = (err as { requestId?: unknown }).requestId;
      if (typeof id === "string" && id.trim().length > 0) return id.trim();
    }
  }
  const fromHeader = header?.trim() ?? "";
  return fromHeader.length > 0 ? fromHeader : null;
}

function adminApiError(response: Response, body: unknown): AdminApiError {
  return new AdminApiError(response.status, body, response.headers.get("X-Request-ID"));
}

/** Wire shape of GET /v1/admin/counterparty-aliases items. */
export interface CounterpartyAlias {
  id: number;
  aliasText: string;
  companySlug: CompanySlug;
  companyDisplayName: string;
  confirmedBy: string;
  confirmedAt: string;
  active: boolean;
  detachedBy: string | null;
  detachedAt: string | null;
}

/** Wire shape of GET /v1/admin/recognized-counterparties items. */
export interface RecognizedCounterparty {
  id: number;
  labelText: string;
  originCompany: CompanySlug;
  originQueueId: number;
  originPlaidTransactionId: string;
  originContextOriginId: string | null;
  reviewedFromLabel: string;
  reviewedToLabel: string;
  reviewedCharacter: string | null;
  reviewedAmountDecimal: string | null;
  reviewedOccurredOn: string | null;
  confirmedBy: string;
  confirmedAt: string;
  active: boolean;
  withdrawnBy: string | null;
  withdrawnAt: string | null;
}

/** How AdminClient reaches the network. Every call goes through one of these. */
type Transport = (url: string, init: RequestInit) => Promise<Response>;

async function postLinkJson(
  send: Transport,
  apiBase: string,
  path: string,
  body: unknown,
): Promise<unknown> {
  if (!isLinkApiPath(path)) {
    throw new Error(`AdminClient: refusing non-link path ${path}`);
  }
  const response = await send(`${apiBase.replace(/\/+$/, "")}${path}`, {
    method: "POST",
    credentials: ADMIN_FETCH_CREDENTIALS,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errBody: unknown = await response.json().catch(() => null);
    throw adminApiError(response, errBody);
  }
  return (await response.json()) as unknown;
}

/**
 * Server-side hard cap on `GET /v1/admin/queue` (`QUEUE_LIST_*_SQL` ends
 * `LIMIT 500` in ingest-worker). The response is `{ items }` only — no
 * `has_more` / total — so the portal treats `items.length >=` this limit as
 * "cap fired" and must disclose it (M1 / R11B-2). Do not invent a softer
 * client page size; this constant mirrors the worker LIMIT exactly.
 */
export const FLEET_QUEUE_LIST_LIMIT = 500;

/** True when a fleet queue page is at or past the known server LIMIT. */
export function fleetQueueListCapFired(itemCount: number): boolean {
  return itemCount >= FLEET_QUEUE_LIST_LIMIT;
}

/**
 * Mirrors ingest-worker/src/receipt_review.ts list caps. Responses are
 * `{ items }` (or `count: items.length`) with no has_more — disclose when
 * length reaches the known LIMIT (same pattern as FLEET_QUEUE_LIST_LIMIT).
 */
export const RECEIPT_SHELF_LIST_LIMIT = 200;
export const RECEIPT_SHELF_CANDIDATE_LIST_LIMIT = 100;
export const RECEIPT_SHELF_RESEARCH_LIST_LIMIT = 50;
/** Days after receipt_date before a row is eligible for the shelf list. */
export const RECEIPT_SHELF_ELIGIBLE_AFTER_DAYS = 4;

export function receiptShelfListCapFired(itemCount: number): boolean {
  return itemCount >= RECEIPT_SHELF_LIST_LIMIT;
}

export function receiptShelfCandidateListCapFired(itemCount: number): boolean {
  return itemCount >= RECEIPT_SHELF_CANDIDATE_LIST_LIMIT;
}

export function receiptShelfResearchListCapFired(itemCount: number): boolean {
  return itemCount >= RECEIPT_SHELF_RESEARCH_LIST_LIMIT;
}

/**
 * Server-side hard cap on admin open hub-questions list (`LIMIT 200` in
 * ingest-worker hub_questions / admin). Response is `{ questions }` with no
 * `has_more` — treat length ≥ this limit as cap fired (Waiting disclosure).
 * Named without QUEUE so queue_limit_drift_lint's review_queue population
 * does not absorb it (different table; residual noted in that lint).
 */
export const HUB_QUESTIONS_LIST_LIMIT = 200;

export function hubQuestionsListCapFired(itemCount: number): boolean {
  return itemCount >= HUB_QUESTIONS_LIST_LIMIT;
}

/** POST /v1/admin/{company}/queue/bulk-resolve. Lockstep with bulkResolveResponseSchema. */
export interface BulkResolveResult {
  resolved: number[];
  skipped: Array<{ id: number; reason: string }>;
}

/**
 * Wire shape of GET /v1/admin/ops-events (books-api.yaml OpsEvent).
 * This route is snake_case. The admin list ignores `limit` and pages 50.
 */
export interface OpsEventRecord {
  id: string;
  fingerprint: string;
  occurred_at: string;
  source: string;
  severity: string;
  code: string;
  where_at: string;
  company: string | null;
  request_id: string | null;
  run_ref: string | null;
  run_url: string | null;
  http_status: number | null;
  message: string | null;
  detail: Record<string, unknown> | null;
  stack: string | null;
}

export interface OpsEventsPage {
  events: OpsEventRecord[];
  next_cursor: string | null;
}

export interface OpsEventQuery {
  since?: string;
  severity?: "error" | "warn" | "info";
  company?: string;
  code?: string;
  cursor?: string;
}

/** GL-21 prior-import wire shapes. CamelCase matches books-api.yaml. */
export type PriorImportFileKind = "csv" | "xlsx";
export type PriorImportFormat = "gv-template" | "wave" | "buildium";
export type PriorImportStatus = "uploaded" | "parsing" | "parsed" | "failed";
export type PriorBookingStatus =
  | "unmatched"
  | "matched"
  | "ambiguous"
  | "booked"
  | "queued"
  | "excluded";
export type PriorMapKind = "account" | "location" | "bank";

export interface PriorImportUploadUrlRequest {
  fileKind: PriorImportFileKind;
  format: PriorImportFormat;
  fileSha256: string;
}

export interface PriorImportUploadUrlResponse {
  uploadUrl: string;
  importId: string;
  expiresAt: string;
}

export interface PriorImportRecord {
  id: string;
  company: CompanySlug;
  uploadedBy: string;
  uploadedAt: string;
  fileSha256: string;
  fileKind: PriorImportFileKind;
  format: PriorImportFormat;
  status: PriorImportStatus;
  errorCode: string | null;
  bookingCount: number | null;
  dateMin: string | null;
  dateMax: string | null;
  fileDeletedAt: string | null;
}

export interface PriorBookingLine {
  lineNo: number;
  extAccount: string;
  amountCents: number;
  extClass: string | null;
}

export interface PriorBooking {
  id: string;
  importId: string;
  company: CompanySlug;
  extRef: string;
  txnDate: string;
  bankLast4: string | null;
  bankName: string | null;
  amountCents: number;
  checkNumber: string | null;
  payee: string | null;
  status: PriorBookingStatus;
  statusReason: string | null;
  matchedPlaidTransactionId: string | null;
  matchedAt: string | null;
  lines: PriorBookingLine[];
}

/** GL-27 booking comparison wire shape. CamelCase matches books-api.yaml. */
export type ComparisonPath = "import" | "rule" | "hub" | "ai" | "person";

export interface BookingComparison {
  company: CompanySlug;
  plaidTransactionId: string;
  priorBookingId: string;
  importedAccount: string;
  bookedAccount: string | null;
  bookedPath: ComparisonPath | null;
  aiAccount: string | null;
  aiConfidence: string | null;
  comparedAt: string;
}

export interface BookingComparisonsPage {
  comparisons: BookingComparison[];
  nextCursor: string | null;
}

export interface PriorImportsPage {
  imports: PriorImportRecord[];
  nextCursor: string | null;
}

export interface PriorImportDetail {
  import: PriorImportRecord;
  bookings: PriorBooking[];
  nextCursor: string | null;
}

export interface PriorAccountMapRow {
  kind: PriorMapKind;
  externalName: string;
  booksValue: string | null;
  bookingCount: number;
}

export interface PriorAccountMapGet {
  rows: PriorAccountMapRow[];
}

export interface PriorAccountMapPut {
  mappings: Array<{
    kind: PriorMapKind;
    externalName: string;
    booksValue: string;
  }>;
}

export interface PriorCoverageBank {
  bankLast4: string | null;
  bankName: string | null;
  booksStart: string | null;
  earliestPostedDate: string | null;
  historicalPullComplete: boolean | null;
}

export interface PriorCoverage {
  banks: PriorCoverageBank[];
}

export interface OpenAccountRow {
  account: string;
  openedOn: string;
  refreshedAt: string;
}

export interface OpenAccountsGet {
  accounts: OpenAccountRow[];
}

/**
 * Admin GET routes whose answers the client may reuse for up to 30 s (rd-x2;
 * rules in read-cache.ts). Matched on the path without its query; the cache key
 * is still the full path and query. These are the reads measured repeating
 * across navigation. A route that is POLLED while waiting for a change must
 * never be listed: check-now (ui/check-now.ts), fleet health (the bank-link
 * wait in pages/connect-account.ts), OCR, statement checks and prior imports
 * are left out for that reason. Leaving a read out only costs a request.
 */
const CACHED_ADMIN_READS: readonly RegExp[] = [
  /^\/v1\/admin\/companies$/,
  /^\/v1\/admin\/dashboard-groups$/,
  /^\/v1\/admin\/queue$/,
  /^\/v1\/admin\/counterparty-aliases$/,
  /^\/v1\/admin\/recognized-counterparties$/,
  /^\/v1\/admin\/onboarding\/[^/]+\/status$/,
  /^\/v1\/admin\/rules\/[^/]+\/requests$/,
  /^\/v1\/admin\/[^/]+\/hub-intake$/,
  /^\/v1\/admin\/[^/]+\/chart-snapshot$/,
  /^\/v1\/admin\/[^/]+\/report-windows$/,
];

export function isCachedAdminRead(path: string): boolean {
  const bare = path.split("?", 1)[0] ?? path;
  return CACHED_ADMIN_READS.some((pattern) => pattern.test(bare));
}

export interface AdminClientOptions {
  /** Test seam for the 30 s read cache (clock, or ttlMs: 0 to turn it off). */
  readCache?: ReadCacheOptions;
}

export class AdminClient {
  private readonly apiBase: string;
  private readonly token: string;
  private readonly consoleSource: ConsoleDataSource;
  private readonly readCache: ReadCache;
  /**
   * How many writes this client has sent. A page that remembers the reads it
   * made (Reports keeps a year's months for the visit) compares this to drop
   * that memory the moment the books change under it — see `writes`.
   */
  private writeCount = 0;

  constructor(
    apiBase: string,
    token: string,
    consoleSource?: ConsoleDataSource,
    options: AdminClientOptions = {},
  ) {
    this.apiBase = apiBase.replace(/\/+$/, "");
    this.token = token;
    this.consoleSource = consoleSource ?? createHttpConsoleDataSource(this.apiBase, this.token);
    this.readCache = new ReadCache(options.readCache);
  }

  /**
   * Writes sent through this client, read or failed. A page compares it with
   * the value it saw when it read something: a difference means a write may
   * have changed the books, so a remembered read is no longer worth serving.
   * Never interpreted as a count of anything a person is shown.
   */
  get writes(): number {
    return this.writeCount;
  }

  /**
   * Forget every reused read. The shell calls this when Refresh or a Try again
   * button is pressed, so those always reach the server.
   */
  clearReadCache(): void {
    this.readCache.invalidate();
  }

  /**
   * The only place AdminClient touches the network (read-cache.test.ts fails
   * if api.ts calls fetch anywhere else). Anything but GET or HEAD is a write,
   * and the read cache is cleared when its answer arrives, failed or not. Every
   * read begun before that moment, including one still in flight, is dropped,
   * so the first read after a write always asks the server. The write count
   * moves at the same moment, for a page that keeps its own memory of reads.
   */
  private readonly send: Transport = async (url, init) => {
    const method = (init.method ?? "GET").toUpperCase();
    if (method === "GET" || method === "HEAD") return fetch(url, init);
    try {
      return await fetch(url, init);
    } finally {
      this.readCache.invalidate();
      this.writeCount += 1;
    }
  };

  /**
   * Console-source reads that repeat across pages, cached by method and
   * arguments: the P&L statement and ledger accounts (Home, Books, Reports,
   * Bank accounts), one Books page, one report and Months. Left out on
   * purpose: balances (the cash figure is re-read on every visit), the
   * Activity list and both searches, which the page re-reads on an explicit
   * action (a date preset that leaves All months, "search again") and which
   * must then reach the server.
   */
  private sourceRead<T>(name: string, args: readonly unknown[], load: () => Promise<T>): Promise<T> {
    return this.readCache.read(`source:${name}:${JSON.stringify(args)}`, load);
  }

  // --- Console data (reports / GL / transactions / balances) ---------------
  // Live path: HttpConsoleDataSource → /v1/admin/{company}/* only.

  getReport<K extends ReportId>(
    company: CompanySlug,
    report: K,
    period: string,
  ): Promise<ReportSnapshot<ReportDataMap[K]>> {
    return this.sourceRead("getReport", [company, report, period], () =>
      this.consoleSource.getReport(company, report, period),
    );
  }

  getGeneralLedger(company: CompanySlug, query: GeneralLedgerQuery = {}): Promise<GeneralLedgerPage> {
    return this.consoleSource.getGeneralLedger(company, query);
  }

  listTransactions(
    company: CompanySlug,
    query: TransactionsQuery = {},
  ): Promise<ConsoleTransactionsPage> {
    return this.consoleSource.listTransactions(company, query);
  }

  getBalances(company: CompanySlug): Promise<BalancesSnapshot> {
    return this.consoleSource.getBalances(company);
  }

  getBalanceHistory(company: CompanySlug, accountName: string): Promise<BalanceHistory> {
    return this.consoleSource.getBalanceHistory(company, accountName);
  }

  listActivity(company: CompanySlug, query: ActivityQuery): Promise<ActivityPage> {
    const source = this.consoleSource;
    if (source.listActivity === undefined) return Promise.reject(new ConsoleDataError(501));
    return source.listActivity(company, query);
  }

  getLedger(company: CompanySlug, query: LedgerQuery): Promise<LedgerEntriesPage> {
    const source = this.consoleSource;
    const read = source.getLedger?.bind(source);
    if (read === undefined) return Promise.reject(new ConsoleDataError(501));
    return this.sourceRead("getLedger", [company, query], () => read(company, query));
  }

  getLedgerAccounts(company: CompanySlug, period: string): Promise<LedgerAccountsResponse> {
    const source = this.consoleSource;
    const read = source.getLedgerAccounts?.bind(source);
    if (read === undefined) return Promise.reject(new ConsoleDataError(501));
    return this.sourceRead("getLedgerAccounts", [company, period], () => read(company, period));
  }

  getLedgerStatement(
    company: CompanySlug,
    report: LedgerStatementReport,
    period: string,
  ): Promise<LedgerStatement> {
    const source = this.consoleSource;
    const read = source.getLedgerStatement?.bind(source);
    if (read === undefined) return Promise.reject(new ConsoleDataError(501));
    return this.sourceRead("getLedgerStatement", [company, report, period], () =>
      read(company, report, period),
    );
  }

  getMonths(company: CompanySlug): Promise<AdminMonthsResponse> {
    const source = this.consoleSource;
    const read = source.getMonths?.bind(source);
    if (read === undefined) return Promise.reject(new ConsoleDataError(501));
    return this.sourceRead("getMonths", [company], () => read(company));
  }

  searchActivity(
    company: CompanySlug,
    query: ActivitySearchQuery,
  ): Promise<ActivitySearchResponse> {
    const source = this.consoleSource;
    if (source.searchActivity === undefined) return Promise.reject(new ConsoleDataError(501));
    return source.searchActivity(company, query);
  }

  searchLedger(company: CompanySlug, query: LedgerSearchQuery): Promise<LedgerSearchResponse> {
    const source = this.consoleSource;
    if (source.searchLedger === undefined) return Promise.reject(new ConsoleDataError(501));
    return source.searchLedger(company, query);
  }

  listReportWindows(company: CompanySlug, report?: string): Promise<ReportWindowCatalog> {
    const qs = new URLSearchParams();
    if (report) qs.set("report", report);
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.request("GET", `/v1/admin/${encodeURIComponent(company)}/report-windows${suffix}`);
  }

  requestReportWindow(
    company: CompanySlug,
    body: { report: string; windowToken: string },
  ): Promise<ReportWindowRequestResult> {
    return this.request("POST", `/v1/admin/${encodeURIComponent(company)}/report-windows`, body);
  }

  async getReportWindow(company: CompanySlug, id: string): Promise<ReportWindowDownload> {
    const path = `/v1/admin/${encodeURIComponent(company)}/report-windows/${encodeURIComponent(id)}`;
    if (!isAdminApiPath(path)) {
      throw new Error(`AdminClient: refusing non-admin path ${path}`);
    }
    const response = await this.send(`${this.apiBase}${path}`, {
      method: "GET",
      credentials: ADMIN_FETCH_CREDENTIALS,
      headers: adminRequestHeaders(this.token, {
        Accept: "application/json, text/csv",
      }),
    });
    if (!response.ok) {
      const errBody: unknown = await response.json().catch(() => null);
      throw adminApiError(response, errBody);
    }
    const text = await response.text();
    const contentType = response.headers.get("Content-Type") ?? "";
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const filenameMatch = /filename="([^"]+)"/.exec(disposition);
    const filename = filenameMatch?.[1] ?? `report-window-${id}`;
    if (contentType.includes("text/csv")) {
      return { contentType, filename, text };
    }
    return { contentType, filename, text, json: JSON.parse(text) as unknown };
  }

  listQueue(params: {
    company?: CompanySlug;
    status?: string;
    escalated?: boolean;
    /** G5. ISO date-time. Do not send together with status (the server returns 400). */
    resolvedSince?: string;
    /**
     * G6. Only true is sent. Do not combine with status or resolvedSince:
     * the server returns 400.
     */
    dismissed?: boolean;
  } = {}): Promise<{ items: QueueItem[] }> {
    const query = new URLSearchParams();
    if (params.company) query.set("company", params.company);
    if (params.status) query.set("status", params.status);
    if (params.escalated !== undefined) {
      query.set("escalated", params.escalated ? "true" : "false");
    }
    if (params.resolvedSince) query.set("resolvedSince", params.resolvedSince);
    if (params.dismissed === true) query.set("dismissed", "true");
    const qs = query.toString();
    return this.request("GET", `/v1/admin/queue${qs ? `?${qs}` : ""}`);
  }

  /**
   * G11. What the hub sent this company for one month, read-only.
   * limit is sent only as an integer 0-200; anything else throws before the
   * request so no malformed value reaches the Worker.
   */
  listHubIntake(
    company: CompanySlug,
    query: HubIntakeQuery = {},
  ): Promise<AdminHubIntakeResponse> {
    const params = new URLSearchParams();
    if (query.period !== undefined) params.set("period", query.period);
    if (query.state !== undefined) params.set("state", query.state);
    if (query.kind !== undefined) params.set("kind", query.kind);
    if (query.limit !== undefined) {
      if (
        !Number.isInteger(query.limit) ||
        query.limit < 0 ||
        query.limit > 200
      ) {
        throw new Error(
          `AdminClient: hub-intake limit must be an integer 0-200, got ${String(query.limit)}`,
        );
      }
      params.set("limit", String(Math.trunc(query.limit)));
    }
    const qs = params.toString();
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/hub-intake${qs ? `?${qs}` : ""}`,
    );
  }

  resolveQueueItem(id: number, body: QueueResolution): Promise<QueueItem> {
    return this.request("POST", `/v1/admin/queue/${String(id)}/resolve`, body);
  }

  /** G6. Reopen a dismissed Moov wallet card. Body is exactly {}. */
  reopenQueueItem(company: CompanySlug, id: number): Promise<QueueItem> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/queue/${String(id)}/reopen`,
      {},
    );
  }

  bulkResolveQueue(
    company: CompanySlug,
    ids: readonly number[],
  ): Promise<BulkResolveResult> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/queue/bulk-resolve`,
      { ids },
    );
  }

  getChartSnapshot(company: CompanySlug): Promise<ChartSnapshot> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/chart-snapshot`,
    );
  }

  getRecurring(company: CompanySlug): Promise<RecurringAdminGet> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/recurring`,
    );
  }

  requestRecurringRun(
    company: CompanySlug,
    period: string,
  ): Promise<RecurringRunRequestResult> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/recurring`,
      { period },
    );
  }

  /** When this company was last checked and how it went. Reads our database only. */
  getCheckNow(company: CompanySlug): Promise<CheckNowStatus> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/check-now`,
    );
  }

  /**
   * Ask Moov and Plaid for this company now, then start booking. The company is
   * the path only; the body is always empty (the server derives who pressed).
   */
  requestCheckNow(company: CompanySlug): Promise<CheckNowStatus> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/check-now`,
      {},
    );
  }

  getVendorIdentities(company: CompanySlug): Promise<VendorIdentitySnapshotGet> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/vendor-identities`,
    );
  }

  listPriorPaymentCandidates(
    company: CompanySlug,
    period: string,
  ): Promise<PriorPaymentCandidatesResponse> {
    const query = new URLSearchParams({ period });
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/prior-payments/candidates?${query.toString()}`,
    );
  }

  attachPriorPayments(
    company: CompanySlug,
    body: { vendorId: string; plaidIds: string[] },
  ): Promise<{ company: CompanySlug; vendorId: string; plaidIds: string[] }> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/prior-payment-attachments`,
      body,
    );
  }

  detachPriorPayment(
    company: CompanySlug,
    body: { plaidId: string },
  ): Promise<{ company: CompanySlug; plaidId: string }> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/prior-payment-attachments/detach`,
      body,
    );
  }

  escalateQueueItem(
    company: CompanySlug,
    id: number,
    body: { note: string },
  ): Promise<QueueItem> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/queue/${String(id)}/escalate`,
      body,
    );
  }

  getReceiptMatchCorrection(
    company: CompanySlug,
    id: number,
  ): Promise<ReceiptMatchCorrectionPage> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/queue/${String(id)}/receipt-match-correction`,
    );
  }

  correctReceiptMatch(
    company: CompanySlug,
    id: number,
    body: { plaidTransactionId: string },
  ): Promise<QueueItem> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/queue/${String(id)}/receipt-match-correction`,
      body,
    );
  }

  returnQueueItemToAm(
    company: CompanySlug,
    id: number,
    body: { note?: string } = {},
  ): Promise<QueueItem> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/queue/${String(id)}/return-to-am`,
      body,
    );
  }

  escalateQueueItemToPractice(
    company: CompanySlug,
    id: number,
    body: QueueEscalateToPracticeRequest = {},
  ): Promise<QueueEscalateToPracticeResponse> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/queue/${String(id)}/escalate-to-practice`,
      body,
    );
  }

  /**
   * The standing rules, plus the card answers whose rule is not in rules.yaml
   * yet (or was not added and why) — one read for the whole page (rd r13 L6).
   *
   * `cardAsks` is optional in the type even though the contract always sends
   * it: the portal is deployed separately from the Worker, so for the length of
   * a deploy the page receives the older body. The page must keep listing the
   * rules in that window instead of blanking (rd r13 L6 review 1).
   */
  listRules(
    company: CompanySlug,
  ): Promise<{ company: CompanySlug; rules: Rule[]; cardAsks?: CardRuleAsk[] }> {
    return this.request("GET", `/v1/admin/rules/${company}`);
  }

  listRuleChangeRequests(
    company: CompanySlug,
    params: { status?: RuleChangeRequestStatusFilter } = {},
  ): Promise<{ company: CompanySlug; requests: RuleChangeRequest[] }> {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    const qs = query.toString();
    return this.request(
      "GET",
      `/v1/admin/rules/${encodeURIComponent(company)}/requests${qs ? `?${qs}` : ""}`,
    );
  }

  decideRuleChangeRequest(
    company: CompanySlug,
    id: string,
    body: { decision: "approve" | "reject"; note?: string },
  ): Promise<RuleChangeRequest> {
    return this.request(
      "POST",
      `/v1/admin/rules/${encodeURIComponent(company)}/requests/${encodeURIComponent(id)}/decision`,
      body,
    );
  }

  dismissRuleChangeRequest(
    company: CompanySlug,
    id: string,
  ): Promise<RuleChangeRequest> {
    return this.request(
      "POST",
      `/v1/admin/rules/${encodeURIComponent(company)}/requests/${encodeURIComponent(id)}/dismiss`,
    );
  }

  listLedgerChangeRequests(
    company: CompanySlug,
    target: LedgerChangeTarget,
    params: { status?: RuleChangeRequestStatusFilter } = {},
  ): Promise<{
    company: CompanySlug;
    target: LedgerChangeTarget;
    requests: LedgerChangeRequest[];
  }> {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    const qs = query.toString();
    return this.request(
      "GET",
      `/v1/admin/change-requests/${encodeURIComponent(company)}/${encodeURIComponent(target)}${qs ? `?${qs}` : ""}`,
    );
  }

  proposeLedgerChange(
    company: CompanySlug,
    target: LedgerChangeTarget,
    body: LedgerChangePropose,
  ): Promise<LedgerChangeRequest> {
    return this.request(
      "POST",
      `/v1/admin/change-requests/${encodeURIComponent(company)}/${encodeURIComponent(target)}`,
      body,
    );
  }

  decideLedgerChangeRequest(
    company: CompanySlug,
    target: LedgerChangeTarget,
    id: string,
    body: { decision: "approve" | "reject"; note?: string },
  ): Promise<LedgerChangeRequest> {
    return this.request(
      "POST",
      `/v1/admin/change-requests/${encodeURIComponent(company)}/${encodeURIComponent(target)}/${encodeURIComponent(id)}/decision`,
      body,
    );
  }

  listReceiptShelf(company: CompanySlug): Promise<{
    items: ReceiptShelfItem[];
    archives: { notOurs: number; duplicate: number };
  }> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/receipt-shelf`,
    );
  }

  listReceiptShelfArchive(
    company: CompanySlug,
    archive: ReceiptShelfArchive,
  ): Promise<{
    archive: ReceiptShelfArchive;
    items: ReceiptShelfItem[];
    count: number;
  }> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/receipt-shelf/archives/${encodeURIComponent(archive)}`,
    );
  }

  listReceiptShelfCandidates(
    company: CompanySlug,
    receiptId: number,
  ): Promise<{
    receipt: ReceiptShelfItem;
    candidates: ReceiptShelfCandidate[];
    preselectedPlaidTransactionId: null;
  }> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/receipt-shelf/${String(receiptId)}/candidates`,
    );
  }

  searchReceiptShelfTransactions(
    company: CompanySlug,
    q: string,
  ): Promise<{ items: ReceiptShelfCandidate[] }> {
    const query = new URLSearchParams({ q });
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/receipt-shelf/transactions/search?${query.toString()}`,
    );
  }

  async tagReceiptShelfItem(
    company: CompanySlug,
    receiptId: number,
    plaidTransactionId: string,
  ): Promise<
    | { tagged: true; receipt: ReceiptShelfItem }
    | { tagged: false; warnings: ReceiptShelfWarning[] }
  > {
    try {
      return await this.request(
        "POST",
        `/v1/admin/${encodeURIComponent(company)}/receipt-shelf/${String(receiptId)}/tag`,
        { plaidTransactionId },
      );
    } catch (error) {
      if (
        error instanceof AdminApiError &&
        error.status === 409 &&
        error.body !== null &&
        typeof error.body === "object" &&
        (error.body as { tagged?: unknown }).tagged === false
      ) {
        return error.body as {
          tagged: false;
          warnings: ReceiptShelfWarning[];
        };
      }
      throw error;
    }
  }

  dismissReceiptShelfItem(
    company: CompanySlug,
    receiptId: number,
    archive: ReceiptShelfArchive,
  ): Promise<{ receipt: ReceiptShelfItem }> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/receipt-shelf/${String(receiptId)}/dismiss`,
      { archive },
    );
  }

  restoreReceiptShelfItem(
    company: CompanySlug,
    receiptId: number,
  ): Promise<{ receipt: ReceiptShelfItem }> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/receipt-shelf/${String(receiptId)}/restore`,
      {},
    );
  }

  requestRuleChange(company: CompanySlug, body: RuleChange): Promise<RuleChangeRequest> {
    return this.request("POST", `/v1/admin/rules/${company}`, body);
  }

  getFleetHealth(): Promise<{ companies: CompanyHealth[] }> {
    return this.request("GET", "/v1/admin/health");
  }

  getIntercompany(): Promise<IntercompanySnapshot> {
    return this.request("GET", "/v1/admin/intercompany");
  }

  listTokens(): Promise<{ tokens: TokenInfo[] }> {
    return this.request("GET", "/v1/admin/tokens");
  }

  issueToken(body: TokenIssueRequest): Promise<TokenIssueResponse> {
    return this.request("POST", "/v1/admin/tokens", body);
  }

  revokeToken(id: string): Promise<TokenInfo> {
    return this.request("POST", `/v1/admin/tokens/${encodeURIComponent(id)}/revoke`);
  }

  listCompanies(): Promise<{ companies: Company[] }> {
    return this.request("GET", "/v1/admin/companies");
  }

  /** GET /v1/admin/company-drafts — server inventory (AM or Owner). */
  listCompanyDrafts(): Promise<{ drafts: CompanyDraft[] }> {
    return this.request("GET", "/v1/admin/company-drafts");
  }

  /** GET /v1/admin/onboarding/{slug}/status — recorded checklist (no re-check). */
  getOnboardingStatus(slug: CompanySlug): Promise<OnboardingStatus> {
    return this.request(
      "GET",
      `/v1/admin/onboarding/${encodeURIComponent(slug)}/status`,
    );
  }

  /** POST /v1/admin/onboarding/runs — create the run record only. */
  createOnboardingRun(body: OnboardingRunCreate): Promise<OnboardingRunResponse> {
    return this.request("POST", "/v1/admin/onboarding/runs", body);
  }

  /** POST /v1/admin/onboarding/{slug}/check — re-run verifiers, return status. */
  checkOnboardingRun(slug: CompanySlug): Promise<OnboardingRunResponse> {
    return this.request(
      "POST",
      `/v1/admin/onboarding/${encodeURIComponent(slug)}/check`,
    );
  }

  /** POST /v1/admin/onboarding/{slug}/steps/{key}/confirm — owner hand tick. */
  confirmOnboardingStep(
    slug: CompanySlug,
    key: OnboardingStepKey,
  ): Promise<OnboardingRunResponse> {
    return this.request(
      "POST",
      `/v1/admin/onboarding/${encodeURIComponent(slug)}/steps/${encodeURIComponent(key)}/confirm`,
    );
  }

  /** GET /v1/admin/onboarding/agent-identities — owner human session only. */
  listAgentIdentities(): Promise<AgentIdentityList> {
    return this.request("GET", "/v1/admin/onboarding/agent-identities");
  }

  /**
   * POST /v1/admin/onboarding/agent-identities — add one named identity.
   * The bearer is returned once here; never persist or log it.
   */
  createAgentIdentity(body: AgentIdentityCreate): Promise<AgentIdentityCreateResponse> {
    return this.request("POST", "/v1/admin/onboarding/agent-identities", body);
  }

  /** DELETE /v1/admin/onboarding/agent-identities — revoke by commonName. */
  revokeAgentIdentity(
    body: AgentIdentityRevokeRequest,
  ): Promise<AgentIdentityRevokeResponse> {
    return this.request("DELETE", "/v1/admin/onboarding/agent-identities", body);
  }

  /** GET /v1/admin/approvals/{id} — read what the ticket is for (safe on load). */
  getApproval(id: string): Promise<OnboardingApprovalReadResponse> {
    return this.request(
      "GET",
      `/v1/admin/approvals/${encodeURIComponent(id)}`,
    );
  }

  /** POST /v1/admin/approvals/{id}/approve — the owner's own approval click. */
  approveApproval(id: string): Promise<OnboardingApprovalApproveResponse> {
    return this.request(
      "POST",
      `/v1/admin/approvals/${encodeURIComponent(id)}/approve`,
    );
  }

  /**
   * GET /v1/admin/approvals/{id}/reveal — mints and returns prepared secrets
   * once. This is deliberately a GET on the Worker: call it only from an
   * explicit owner click, never on page load.
   */
  revealApproval(id: string): Promise<OnboardingRevealResponse> {
    return this.request(
      "GET",
      `/v1/admin/approvals/${encodeURIComponent(id)}/reveal`,
    );
  }

  /** DELETE /v1/admin/company-drafts/{slug} — discard a saved draft. */
  deleteCompanyDraft(slug: CompanySlug): Promise<OnboardingDraftDeleteResponse> {
    return this.request(
      "DELETE",
      `/v1/admin/company-drafts/${encodeURIComponent(slug)}`,
    );
  }

  /** GET /v1/admin/counterparty-aliases — standing nickname rules (AM or Owner). */
  listCounterpartyAliases(): Promise<{ aliases: CounterpartyAlias[] }> {
    return this.request("GET", "/v1/admin/counterparty-aliases");
  }

  /**
   * POST /v1/admin/counterparty-aliases/{id}/detach — deactivate, never delete.
   * detached_by is server-derived.
   */
  detachCounterpartyAlias(id: number): Promise<CounterpartyAlias> {
    return this.request(
      "POST",
      `/v1/admin/counterparty-aliases/${String(id)}/detach`,
    );
  }

  /** GET /v1/admin/recognized-counterparties — standing party rules (AM or Owner). */
  listRecognizedCounterparties(): Promise<{
    counterparties: RecognizedCounterparty[];
  }> {
    return this.request("GET", "/v1/admin/recognized-counterparties");
  }

  /**
   * POST /v1/admin/recognized-counterparties/{id}/withdraw — deactivate, never delete.
   * withdrawn_by is server-derived.
   */
  withdrawRecognizedCounterparty(id: number): Promise<RecognizedCounterparty> {
    return this.request(
      "POST",
      `/v1/admin/recognized-counterparties/${String(id)}/withdraw`,
    );
  }

  /** POST /v1/admin/companies — live register or asDraft (AM or Owner). */
  createCompany(body: CompanyCreate): Promise<Company | CompanyDraft> {
    return this.request("POST", "/v1/admin/companies", body);
  }

  /**
   * PATCH /v1/admin/companies/{company} — update live or draft;
   * submit:true promotes draft → companies.
   */
  updateCompany(
    company: CompanySlug,
    body: CompanyUpdate,
  ): Promise<Company | CompanyDraft> {
    return this.request(
      "PATCH",
      `/v1/admin/companies/${encodeURIComponent(company)}`,
      body,
    );
  }

  /**
   * Mint a ≤15-min single-object signed upload URL for an opening document.
   * Browser PUTs the file bytes directly at the returned URL (does not proxy
   * through this client as JSON).
   */
  mintOpeningDocumentUploadUrl(
    company: CompanySlug,
    body: OpeningDocumentUploadUrlRequest,
  ): Promise<OpeningDocumentUploadUrlResponse> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/opening-documents/upload-url`,
      body,
    );
  }

  /**
   * Verify an AM-asserted (or engine-extract) quotation against one stored
   * opening document. Returns outcome only — never document text.
   */
  verifyOpeningDocumentQuote(
    company: CompanySlug,
    body: VerifyOpeningDocumentQuoteRequest,
  ): Promise<VerifyOpeningDocumentQuoteResponse> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/opening-documents/verify-quote`,
      body,
    );
  }

  getOpeningDocumentExtract(
    company: CompanySlug,
    kind: OpeningDocumentKind,
    contentSha256Hex: string,
  ): Promise<OpeningDocumentExtractResponse> {
    const params = new URLSearchParams({
      kind,
      contentSha256Hex,
    });
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/opening-documents/extract?${params.toString()}`,
    );
  }

  getOpeningDocumentOcr(
    company: CompanySlug,
    kind: OpeningDocumentKind,
    contentSha256Hex: string,
    field: OpeningOcrDeterminationField,
  ): Promise<OpeningDocumentOcrResponse> {
    const params = new URLSearchParams({
      kind,
      contentSha256Hex,
      field,
    });
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/opening-documents/ocr?${params.toString()}`,
    );
  }

  /**
   * Durable opening-determination evidence for one company (append-only).
   * Quotations are sensitive — this client is admin-only; never log the body.
   */
  listOpeningDeterminations(
    company: CompanySlug,
  ): Promise<OpeningDeterminationsResponse> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/opening-determinations`,
    );
  }

  putOpeningDetermination(
    company: CompanySlug,
    body: OpeningDeterminationPut,
  ): Promise<OpeningDeterminationRecord> {
    return this.request(
      "PUT",
      `/v1/admin/${encodeURIComponent(company)}/opening-determinations`,
      body,
    );
  }

  async putOpeningOcrCrop(
    company: CompanySlug,
    contentSha256Hex: string,
    image: Blob,
    field: OpeningOcrDeterminationField,
  ): Promise<{ status: "pending" }> {
    const params = new URLSearchParams({
      kind: "tax-return",
      contentSha256Hex,
      field,
    });
    const response = await this.send(
      `${this.apiBase}/v1/admin/${encodeURIComponent(company)}/opening-documents/ocr-crop?${params.toString()}`,
      {
        method: "PUT",
        credentials: ADMIN_FETCH_CREDENTIALS,
        headers: adminRequestHeaders(this.token, {
          Accept: "application/json",
          "Content-Type": image.type || "application/octet-stream",
        }),
        body: image,
      },
    );
    if (!response.ok) {
      const errBody: unknown = await response.json().catch(() => null);
      throw adminApiError(response, errBody);
    }
    return (await response.json()) as { status: "pending" };
  }

  /**
   * PUT file bytes at a signed opening-document upload URL. The URL is the
   * credential — no bearer. Path is NOT /v1/admin/* by design. Cross-origin
   * R2; omit cookies (this is not the Access hop).
   */
  async putOpeningDocumentUpload(
    signedUrl: string,
    file: Blob,
  ): Promise<OpeningDocumentUploadResponse> {
    const response = await this.send(signedUrl, {
      method: "PUT",
      credentials: "omit",
      headers: {
        Accept: "application/json",
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });
    if (!response.ok) {
      const errBody: unknown = await response.json().catch(() => null);
      throw adminApiError(response, errBody);
    }
    return (await response.json()) as OpeningDocumentUploadResponse;
  }

  /**
   * The files attached to one transaction, oldest first.
   *
   * The line is named by `source` + `refId` (`ActivityItem.refId`: a Plaid
   * transaction id for a bank line, a Moov transfer id for a wallet payment).
   * Never a raw event row id; the Worker keys attachments on the reference a
   * line keeps when the bank corrects it.
   */
  listTransactionAttachments(
    company: CompanySlug,
    source: TransactionAttachmentSource,
    refId: string,
  ): Promise<TransactionAttachmentListResponse> {
    const q = new URLSearchParams({ source, refId });
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/transaction-attachments?${q.toString()}`,
    );
  }

  /**
   * Attach one file to a transaction (owner decision 8, 2026-11-02).
   *
   * Multipart; do not set Content-Type — the runtime supplies the boundary.
   * The Worker sniffs the bytes, refuses anything that is not a PDF/JPEG/PNG or
   * is over 10 MiB, and keeps the file private. Never surfaces the raw server
   * reason (Law 2).
   */
  async uploadTransactionAttachment(
    company: CompanySlug,
    source: TransactionAttachmentSource,
    refId: string,
    file: File,
  ): Promise<TransactionAttachmentUploadResponse> {
    const q = new URLSearchParams({ source, refId });
    const path =
      `/v1/admin/${encodeURIComponent(company)}/transaction-attachments` +
      `?${q.toString()}`;
    if (!isAdminApiPath(path)) {
      throw new Error(`AdminClient: refusing non-admin path ${path}`);
    }
    const form = new FormData();
    form.append("file", file);
    const response = await this.send(`${this.apiBase}${path}`, {
      method: "POST",
      credentials: ADMIN_FETCH_CREDENTIALS,
      headers: adminRequestHeaders(this.token, { Accept: "application/json" }),
      body: form,
    });
    if (!response.ok) {
      const errBody: unknown = await response.json().catch(() => null);
      throw adminApiError(response, errBody);
    }
    return (await response.json()) as TransactionAttachmentUploadResponse;
  }

  /**
   * Mint a ≤15-minute signed URL for one attachment. The URL opens exactly that
   * file and nothing else; the object key never reaches the browser.
   */
  mintTransactionAttachmentUrl(
    company: CompanySlug,
    attachmentId: string,
  ): Promise<TransactionAttachmentUrlResponse> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/transaction-attachments/` +
        `${encodeURIComponent(attachmentId)}/url`,
    );
  }

  /**
   * Withdraw one attached file (owner amendment, 2026-11-02, GRANT G15b).
   *
   * A withdraw, never a delete: the file stops showing on the transaction and
   * the record of who uploaded it and who withdrew it is kept. The Worker
   * enforces uploader-or-owner; the console only offers what it would allow.
   * The answer is the line's remaining visible attachments.
   */
  withdrawTransactionAttachment(
    company: CompanySlug,
    attachmentId: string,
  ): Promise<TransactionAttachmentListResponse> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/transaction-attachments/` +
        `${encodeURIComponent(attachmentId)}/withdraw`,
    );
  }

  /** Console personas addendum A — role echo for persona IA. */
  getMe(): Promise<AdminMe> {
    return this.request("GET", "/v1/admin/me");
  }

  /** Owner-only dashboard groups (create/edit/set-default; no delete in v1). */
  listDashboardGroups(): Promise<{ groups: DashboardGroup[] }> {
    return this.request("GET", "/v1/admin/dashboard-groups");
  }

  /** Owner-only shared-card list (GL-38). The last-4 travels in the body, not the path. */
  getIcSplitCards(): Promise<IcSplitCardsResponse> {
    return this.request("GET", "/v1/admin/ic-split-cards");
  }

  putIcSplitCard(body: { cardLast4: string; companies: string[] }): Promise<IcSplitCard> {
    return this.request("PUT", "/v1/admin/ic-split-cards", body);
  }

  createDashboardGroup(body: DashboardGroupCreate): Promise<DashboardGroup> {
    return this.request("POST", "/v1/admin/dashboard-groups", body);
  }

  updateDashboardGroup(id: string, body: DashboardGroupUpdate): Promise<DashboardGroup> {
    return this.request(
      "PATCH",
      `/v1/admin/dashboard-groups/${encodeURIComponent(id)}`,
      body,
    );
  }

  setDefaultDashboardGroup(id: string): Promise<DashboardGroup> {
    return this.request(
      "POST",
      `/v1/admin/dashboard-groups/${encodeURIComponent(id)}/set-default`,
    );
  }

  /** Console personas addendum B — Waiting surface (admin:queue). */
  listHubQuestions(
    company: CompanySlug,
    params: { status: "open" } = { status: "open" },
  ): Promise<AdminHubQuestionsResponse> {
    const qs = new URLSearchParams({ status: params.status });
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/hub-questions?${qs}`,
    );
  }

  /** Set assignTo on an open hub question (role-slug only). */
  assignHubQuestion(
    company: CompanySlug,
    externalRef: string,
    body: { assignTo: { role: string } },
  ): Promise<HubQuestionAssignResponse> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/hub-questions/${encodeURIComponent(externalRef)}/assign`,
      body,
    );
  }

  /**
   * Multipart PDF upload → private R2. Do not set Content-Type; the runtime
   * supplies the multipart boundary. Never surfaces the raw server reason.
   *
   * `closingDate` is the closing date printed on a card statement, as the
   * person entered it (`YYYY-MM-DD`); the Worker requires it for a credit-card
   * account and ignores it for a depository or loan account (rd14 T2 fix round
   * 3, owner grant G14-3). Omitted entirely when empty, so no field is sent
   * that the Worker would have to interpret.
   */
  async uploadStatement(
    company: CompanySlug,
    period: string,
    account: string,
    file: File,
    closingDate?: string | null,
  ): Promise<StatementUploadResponse> {
    const path =
      `/v1/admin/${encodeURIComponent(company)}/statements/${encodeURIComponent(period)}` +
      `?account=${encodeURIComponent(account)}`;
    if (!isAdminApiPath(path)) {
      throw new Error(`AdminClient: refusing non-admin path ${path}`);
    }
    const form = new FormData();
    form.append("file", file);
    const entered = (closingDate ?? "").trim();
    if (entered.length > 0) form.append("closing_date", entered);
    const response = await this.send(`${this.apiBase}${path}`, {
      method: "POST",
      credentials: ADMIN_FETCH_CREDENTIALS,
      headers: adminRequestHeaders(this.token, {
        Accept: "application/json",
      }),
      body: form,
    });
    if (!response.ok) {
      const errBody: unknown = await response.json().catch(() => null);
      throw adminApiError(response, errBody);
    }
    return (await response.json()) as StatementUploadResponse;
  }

  listStatementReconciliations(
    company: CompanySlug,
    period: string,
    opts?: { outcome?: string },
  ): Promise<{ company: string; period: string; items: StatementReconRow[] }> {
    const q =
      opts?.outcome !== undefined && opts.outcome.length > 0
        ? `?outcome=${encodeURIComponent(opts.outcome)}`
        : "";
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/statement-reconciliations/${encodeURIComponent(period)}${q}`,
    );
  }

  confirmStatementReconciliation(
    company: CompanySlug,
    id: string,
    body: StatementConfirmRequest,
  ): Promise<StatementConfirmResponse> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/statement-reconciliations/${encodeURIComponent(id)}/confirm`,
      body,
    );
  }

  issueLinkInvitation(
    company: CompanySlug,
    body: { expiresAt: string; label?: string },
  ): Promise<LinkInvitationIssueResponse> {
    return this.request("POST", `/v1/admin/companies/${company}/link-invitations`, body);
  }

  revokeLinkInvitation(id: string): Promise<void> {
    return this.request("POST", `/v1/admin/link-invitations/${encodeURIComponent(id)}/revoke`);
  }

  /**
   * Redeem a console-minted invitation. Same-origin `/link/session` — no
   * admin bearer. Invitation token is the session identity.
   */
  openLinkSession(invitationToken: string): Promise<LinkSessionResponse> {
    return postLinkJson(this.send, this.apiBase, "/link/session", {
      invitation_token: invitationToken,
    }).then((wire) => {
      const body = wire as {
        link_token?: unknown;
        expiration?: unknown;
        hosted_link_url?: unknown;
      };
      if (
        typeof body.link_token !== "string" ||
        typeof body.expiration !== "string" ||
        typeof body.hosted_link_url !== "string" ||
        !body.hosted_link_url.startsWith("https:")
      ) {
        throw new AdminApiError(500, wire);
      }
      return {
        linkToken: body.link_token,
        expiration: body.expiration,
        hostedLinkUrl: body.hosted_link_url,
      };
    });
  }

  /** Owner ops log. Cloudflare Access + admin:console; the Worker requires Owner. */
  listOpsEvents(query: OpsEventQuery = {}): Promise<OpsEventsPage> {
    const qs = new URLSearchParams();
    if (query.since) qs.set("since", query.since);
    if (query.severity) qs.set("severity", query.severity);
    if (query.company) qs.set("company", query.company);
    if (query.code) qs.set("code", query.code);
    if (query.cursor) qs.set("cursor", query.cursor);
    const suffix = qs.toString();
    return this.request("GET", `/v1/admin/ops-events${suffix ? `?${suffix}` : ""}`);
  }

  listExpenseAuditItems(
    company: CompanySlug,
    query: {
      from?: string;
      to?: string;
      view?: string;
      status?: string;
      source?: string;
      location?: string;
      after?: string;
    } = {},
  ): Promise<ExpenseAuditItemsPage> {
    const params = new URLSearchParams();
    if (query.from) params.set("from", query.from);
    if (query.to) params.set("to", query.to);
    if (query.view) params.set("view", query.view);
    if (query.status) params.set("status", query.status);
    if (query.source) params.set("source", query.source);
    if (query.location) params.set("location", query.location);
    if (query.after) params.set("after", query.after);
    const qs = params.toString();
    return this.request<unknown>(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/expense-audit/items${qs ? `?${qs}` : ""}`,
    ).then((body) => parseExpenseAuditItemsPage(body, company));
  }

  postExpenseAuditReview(
    company: CompanySlug,
    targetId: string,
    expectedLegsSha256: string,
  ): Promise<void> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/expense-audit/items/${encodeURIComponent(targetId)}/review`,
      { expected_legs_sha256: expectedLegsSha256 },
    );
  }

  postExpenseAuditDecision(
    company: CompanySlug,
    targetId: string,
    body: ExpenseAuditDecisionBody,
  ): Promise<ExpenseAuditDecisionAccepted> {
    return this.request<unknown>(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/expense-audit/items/${encodeURIComponent(targetId)}/decision`,
      body,
    ).then((wire) => {
      if (!isRecord(wire)) throw new AdminApiError(500, wire);
      const decisionId = readString(wire, "decision_id", "decisionId");
      const status = readString(wire, "status", "status");
      const icRaw = readField(wire, "ic_pair_id", "icPairId");
      const icPairId = icRaw === null ? null : typeof icRaw === "string" ? icRaw : null;
      if (decisionId === null || status !== "pending_apply") {
        throw new AdminApiError(500, wire);
      }
      return { decisionId, icPairId, status: "pending_apply" };
    });
  }

  cancelExpenseAuditDecision(company: CompanySlug, decisionId: string): Promise<void> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/expense-audit/decisions/${encodeURIComponent(decisionId)}/cancel`,
    );
  }

  getExpenseAuditSummary(scope: "company" | "group" | "all", id: string): Promise<unknown> {
    const params = new URLSearchParams();
    params.set("scope", scope);
    if (id.length > 0) params.set("id", id);
    return this.request("GET", `/v1/admin/expense-audit/summary?${params.toString()}`);
  }

  listExpenseAuditViews(): Promise<ExpenseAuditViewRecord[]> {
    return this.request<unknown>("GET", "/v1/admin/expense-audit/views").then((body) =>
      parseExpenseAuditViews(body),
    );
  }

  putExpenseAuditView(
    body: { name: string; accountSuffixes: string[]; quickPicks: string[] },
    id?: string,
  ): Promise<ExpenseAuditViewRecord> {
    const wire = {
      name: body.name,
      account_suffixes: body.accountSuffixes,
      quick_picks: body.quickPicks,
    };
    const path =
      id === undefined
        ? "/v1/admin/expense-audit/views"
        : `/v1/admin/expense-audit/views/${encodeURIComponent(id)}`;
    return this.request<unknown>("PUT", path, wire).then((saved) => {
      const parsed = parseExpenseAuditViews({ views: [saved] });
      const first = parsed[0];
      if (!first) throw new AdminApiError(500, saved);
      return {
        ...first,
        accountSuffixes: body.accountSuffixes,
        quickPicks: body.quickPicks,
      };
    });
  }

  mintExpenseAuditDocumentUrls(
    company: CompanySlug,
    documentIds: readonly string[],
  ): Promise<ExpenseAuditSignedUrl[]> {
    return this.request<unknown>(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/expense-audit/document-urls`,
      { document_ids: [...documentIds] },
    ).then((body) => parseExpenseAuditSignedUrls(body));
  }

  /**
   * Consume the invitation and store the Item. Same-origin `/link/exchange`
   * — no admin bearer.
   */
  exchangeLinkPublicToken(body: {
    invitationToken: string;
    publicToken: string;
    institution: string;
  }): Promise<LinkExchangeResponse> {
    return postLinkJson(this.send, this.apiBase, "/link/exchange", {
      invitation_token: body.invitationToken,
      public_token: body.publicToken,
      institution: body.institution,
    }).then((wire) => {
      const parsed = wire as { company?: unknown; plaidItemId?: unknown };
      if (typeof parsed.company !== "string" || typeof parsed.plaidItemId !== "string") {
        throw new AdminApiError(500, wire);
      }
      return { company: parsed.company, plaidItemId: parsed.plaidItemId };
    });
  }

  /**
   * Mint a 15-minute signed URL for one prior-software file.
   * The browser PUTs the bytes at `uploadUrl` (not an admin path).
   */
  mintPriorImportUploadUrl(
    company: CompanySlug,
    body: PriorImportUploadUrlRequest,
  ): Promise<PriorImportUploadUrlResponse> {
    return this.request(
      "POST",
      `/v1/admin/${encodeURIComponent(company)}/prior-imports/upload-url`,
      body,
    );
  }

  /**
   * PUT file bytes at a signed prior-import URL. The URL is the credential.
   * Cross-origin capable; omit cookies (same rule as opening-document upload).
   */
  async putPriorImportFile(uploadUrl: string, file: Blob): Promise<void> {
    const response = await this.send(uploadUrl, {
      method: "PUT",
      credentials: "omit",
      headers: {
        "Content-Type": "application/octet-stream",
      },
      body: file,
    });
    if (!response.ok) {
      const errBody: unknown = await response.json().catch(() => null);
      throw adminApiError(response, errBody);
    }
  }

  listBookingComparisons(
    company: CompanySlug,
    params: {
      month?: string;
      path?: ComparisonPath;
      agree?: boolean;
      cursor?: string;
    } = {},
  ): Promise<BookingComparisonsPage> {
    const query = new URLSearchParams();
    if (params.month !== undefined) query.set("month", params.month);
    if (params.path !== undefined) query.set("path", params.path);
    if (params.agree !== undefined) query.set("agree", params.agree ? "true" : "false");
    if (params.cursor !== undefined) query.set("cursor", params.cursor);
    const qs = query.toString();
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/booking-comparisons${qs ? `?${qs}` : ""}`,
    );
  }

  listPriorImports(
    company: CompanySlug,
    params: { status?: PriorImportStatus; cursor?: string } = {},
  ): Promise<PriorImportsPage> {
    const query = new URLSearchParams();
    if (params.status !== undefined) query.set("status", params.status);
    if (params.cursor !== undefined) query.set("cursor", params.cursor);
    const qs = query.toString();
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/prior-imports${qs ? `?${qs}` : ""}`,
    );
  }

  getPriorImport(
    company: CompanySlug,
    id: string,
    cursor?: string,
  ): Promise<PriorImportDetail> {
    const query = new URLSearchParams();
    if (cursor !== undefined) query.set("cursor", cursor);
    const qs = query.toString();
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/prior-imports/${encodeURIComponent(id)}${qs ? `?${qs}` : ""}`,
    );
  }

  getPriorAccountMap(company: CompanySlug): Promise<PriorAccountMapGet> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/prior-account-map`,
    );
  }

  putPriorAccountMap(
    company: CompanySlug,
    body: PriorAccountMapPut,
  ): Promise<PriorAccountMapGet> {
    return this.request(
      "PUT",
      `/v1/admin/${encodeURIComponent(company)}/prior-account-map`,
      body,
    );
  }

  getPriorCoverage(company: CompanySlug): Promise<PriorCoverage> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/prior-coverage`,
    );
  }

  listOpenAccounts(company: CompanySlug): Promise<OpenAccountsGet> {
    return this.request(
      "GET",
      `/v1/admin/${encodeURIComponent(company)}/open-accounts`,
    );
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    if (!isAdminApiPath(path)) {
      throw new Error(`AdminClient: refusing non-admin path ${path}`);
    }
    if (method === "GET" && body === undefined && isCachedAdminRead(path)) {
      return this.readCache.read(path, () => this.exchange<T>(method, path));
    }
    return this.exchange<T>(method, path, body);
  }

  private async exchange<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers = adminRequestHeaders(this.token, {
      Accept: "application/json",
    });
    const init: RequestInit = {
      method,
      credentials: ADMIN_FETCH_CREDENTIALS,
      headers,
    };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }
    const response = await this.send(`${this.apiBase}${path}`, init);
    if (!response.ok) {
      const errBody: unknown = await response.json().catch(() => null);
      throw adminApiError(response, errBody);
    }
    const text = await response.text();
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function readField(obj: Record<string, unknown>, snake: string, camel: string): unknown {
  if (Object.prototype.hasOwnProperty.call(obj, snake)) return obj[snake];
  if (Object.prototype.hasOwnProperty.call(obj, camel)) return obj[camel];
  return undefined;
}

function readString(obj: Record<string, unknown>, snake: string, camel: string): string | null {
  const value = readField(obj, snake, camel);
  return typeof value === "string" ? value : null;
}

/** A leg. The Worker's `AuditLeg` has no company: the item's is the answer. */
function parseExpenseAuditLeg(value: unknown, fallbackCompany: string): ExpenseAuditLeg | null {
  if (!isRecord(value)) return null;
  const company = readString(value, "company", "company") ?? fallbackCompany;
  const location = readString(value, "location", "location");
  const account = readString(value, "account", "account");
  const amount = readString(value, "amount", "amount");
  if (location === null || account === null || amount === null) return null;
  return { company, location, account, amount };
}

function parseExpenseAuditLegs(value: unknown, fallbackCompany: string): ExpenseAuditLeg[] {
  const legs: ExpenseAuditLeg[] = [];
  if (!Array.isArray(value)) return legs;
  for (const raw of value) {
    const parsed = parseExpenseAuditLeg(raw, fallbackCompany);
    if (parsed) legs.push(parsed);
  }
  return legs;
}

function parseExpenseAuditItemsPage(body: unknown, company: string): ExpenseAuditItemsPage {
  if (!isRecord(body) || !Array.isArray(body.items)) {
    throw new AdminApiError(500, body);
  }
  const items: ExpenseAuditItem[] = [];
  for (const raw of body.items) {
    const item = parseExpenseAuditItem(raw, company);
    if (!item) throw new AdminApiError(500, body);
    items.push(item);
  }
  const nextRaw = readField(body, "next_after", "nextAfter");
  const nextAfter = nextRaw === null ? null : typeof nextRaw === "string" ? nextRaw : null;
  return { items, nextAfter };
}

function parseExpenseAuditItem(value: unknown, itemCompany: string): ExpenseAuditItem | null {
  if (!isRecord(value)) return null;
  const targetKindRaw = readString(value, "target_kind", "targetKind");
  const targetId = readString(value, "target_id", "targetId");
  // R5-01: the Worker's item carries `txn_date`. The portal's older `date` was
  // a legacy alias only until the demo spoke the Worker's shape; R9 removed it
  // (round 11, R9-12), so a rename on the wire now fails loudly instead of
  // loading a page of items with no date.
  const date = readString(value, "txn_date", "txnDate");
  // The D1 column is nullable and the pipeline writes null for an empty payee:
  // a null payee is a readable item with no payee, never an unreadable one.
  const payee = readString(value, "payee", "payee");
  const legsSha = readString(value, "legs_sha256", "legsSha256");
  if (
    (targetKindRaw !== "plaid" && targetKindRaw !== "moov") ||
    targetId === null ||
    date === null ||
    legsSha === null
  ) {
    return null;
  }
  // The Worker's item has no company field and its legs carry none; the read's
  // own company is the only honest source, and a decision must post it back.
  // `current_legs` was R5's legacy alias and R9 removed it (R9-12).
  const currentLegs = parseExpenseAuditLegs(value["legs"], itemCompany);
  const reviewRaw = readString(value, "review_state", "reviewState");
  const reviewState: ExpenseAuditReviewState =
    reviewRaw === "reviewed" || reviewRaw === "changed" ? reviewRaw : "not_reviewed";
  const sourceRaw = readString(value, "source", "source");
  const receipt = parseReceipt(readField(value, "receipt", "receipt"));
  const billLines = parseBillPayment(readField(value, "bill_payment", "billPayment"));
  const documents = parseDocuments(readField(value, "documents", "documents"));
  let source: ExpenseAuditSource;
  if (sourceRaw === "receipt" || sourceRaw === "invoice" || sourceRaw === "no_document") {
    source = sourceRaw;
  } else if (receipt) source = "receipt";
  else if (billLines.length > 0) source = "invoice";
  else source = "no_document";
  const locked = readField(value, "period_locked", "periodLocked");
  return {
    targetKind: targetKindRaw,
    targetId,
    date,
    period: readString(value, "period", "period") ?? "",
    payee,
    narration: readString(value, "narration", "narration"),
    bankAmount: readString(value, "bank_amount_decimal", "bankAmountDecimal") ?? "0.00",
    bookingAccount: readString(value, "booking_account", "bookingAccount"),
    accountMask: readString(value, "account_mask", "accountMask"),
    originalSource: readString(value, "original_source", "originalSource"),
    currentLegs,
    legsSha256: legsSha,
    periodLocked: locked === true,
    reviewState,
    latestDecision: parseDecision(value["decision"], itemCompany),
    receipt,
    billLines,
    documents,
    source,
  };
}

function parseDecision(value: unknown, company: string): ExpenseAuditDecisionView | null {
  if (!isRecord(value)) return null;
  const id = readString(value, "id", "id");
  const statusRaw = readString(value, "status", "status");
  if (id === null || statusRaw === null) return null;
  if (
    statusRaw !== "pending_apply" &&
    statusRaw !== "applied" &&
    statusRaw !== "conflict" &&
    statusRaw !== "failed" &&
    statusRaw !== "cancelled"
  ) {
    return null;
  }
  // The Worker's `pair_state` is its own vocabulary (`failed`, `applied`,
  // `pending`, `conflict`, `cancelled`, `awaiting_counterpart`, or null). The
  // two states the card speaks about map to the pair badge; the rest are "no
  // pair badge", and `ic_pair_id` alone still means Paired.
  const pairRaw = readString(value, "pair_state", "pairState");
  const pairState: ExpenseAuditPairState =
    pairRaw === "awaiting_counterpart"
      ? "awaiting_counterpart"
      : pairRaw === "paired" || pairRaw === "applied"
        ? "paired"
        : pairRaw === "failed" || pairRaw === "failed_counterpart"
          ? "failed_counterpart"
          : "none";
  const newLegs = parseExpenseAuditLegs(value["new_legs"] ?? value["newLegs"], company);
  const icRaw = readField(value, "ic_pair_id", "icPairId");
  return {
    id,
    status: statusRaw,
    statusReason: readString(value, "status_reason", "statusReason"),
    newLegs,
    pairState,
    icPairId: typeof icRaw === "string" ? icRaw : null,
    origin: readString(value, "origin", "origin"),
  };
}

function parseReceipt(value: unknown): ExpenseAuditReceiptFacts | null {
  if (!isRecord(value)) return null;
  const vendor = readString(value, "vendor", "vendor");
  const property = readString(value, "property", "property");
  if (vendor === null || property === null) return null;
  const capital = readField(value, "capital_replacement", "capitalReplacement");
  const glAccount = readString(value, "gl_account", "glAccount");
  const glParent = readString(value, "gl_parent", "glParent");
  return {
    vendor,
    expenseType: readString(value, "expense_type", "expenseType") ?? "",
    subCategory: readString(value, "sub_category", "subCategory"),
    // Absent for the five legacy values; never synthesise an empty name.
    ...(glAccount === null ? {} : { glAccount }),
    ...(glParent === null ? {} : { glParent }),
    capitalReplacement: typeof capital === "boolean" ? capital : null,
    note: readString(value, "note", "note"),
    property,
    address: readString(value, "address", "address") ?? readString(value, "sor_address", "sorAddress"),
    submittedBy: readString(value, "submitted_by", "submittedBy") ?? "",
    imageUrl: readString(value, "image_url", "imageUrl"),
  };
}

/**
 * The Worker's `bill_payment`: `{invoice_number, paid_by, paid_date, lines}`
 * whose lines are the stored bill shape (`gl_account`, `gl_parent`,
 * `amount_cents`, …). A null `bill_payment` is a bill-less item; the portal's
 * older flat `bill_lines` alias is gone (round 11, R9-12).
 */
function parseBillPayment(value: unknown): ExpenseAuditBillLine[] {
  if (!isRecord(value)) return [];
  const lines = value["lines"];
  const paidBy = readString(value, "paid_by", "paidBy");
  const out: ExpenseAuditBillLine[] = [];
  if (Array.isArray(lines)) {
    for (const raw of lines) {
      if (!isRecord(raw)) continue;
      const amountCents = readField(raw, "amount_cents", "amountCents");
      out.push({
        property: readString(raw, "property", "property") ?? "",
        unit: readString(raw, "unit", "unit"),
        hubAccount:
          readString(raw, "gl_account", "glAccount") ??
          readString(raw, "gl_parent", "glParent") ??
          "",
        description: readString(raw, "description", "description") ?? "",
        amount: typeof amountCents === "number" ? centsText(amountCents) : "",
        paidBy,
      });
    }
  }
  return out;
}

/** Cents as the decimal string the money helpers read: 123456 -> "1234.56". */
function centsText(cents: number): string {
  const negative = cents < 0;
  const abs = Math.abs(Math.trunc(cents));
  const whole = Math.floor(abs / 100);
  const frac = String(abs % 100).padStart(2, "0");
  return `${negative ? "-" : ""}${String(whole)}.${frac}`;
}

function parseDocuments(value: unknown): ExpenseAuditDocumentRef[] {
  if (!Array.isArray(value)) return [];
  const docs: ExpenseAuditDocumentRef[] = [];
  for (const raw of value) {
    if (!isRecord(raw)) continue;
    const documentId = readString(raw, "document_id", "documentId");
    const contentType = readString(raw, "content_type", "contentType");
    if (documentId === null || contentType === null) continue;
    const seqRaw = readField(raw, "seq", "seq");
    const countRaw = readField(raw, "declared_count", "declaredCount");
    docs.push({
      documentId,
      contentType,
      seq: typeof seqRaw === "number" ? seqRaw : 0,
      declaredCount: typeof countRaw === "number" ? countRaw : 0,
    });
  }
  return docs;
}

function parseExpenseAuditViews(body: unknown): ExpenseAuditViewRecord[] {
  const list = isRecord(body) && Array.isArray(body.views)
    ? body.views
    : Array.isArray(body)
      ? body
      : null;
  if (list === null) return [];
  const views: ExpenseAuditViewRecord[] = [];
  for (const raw of list) {
    if (!isRecord(raw)) continue;
    const id = readString(raw, "id", "id");
    const name = readString(raw, "name", "name");
    if (id === null || name === null) continue;
    views.push({
      id,
      name,
      accountSuffixes: readStringList(raw, "account_suffixes", "accountSuffixes"),
      quickPicks: readStringList(raw, "quick_picks", "quickPicks"),
    });
  }
  return views;
}

function readStringList(obj: Record<string, unknown>, snake: string, camel: string): string[] {
  const value = readField(obj, snake, camel);
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string" && item.length > 0);
}

function parseExpenseAuditSignedUrls(body: unknown): ExpenseAuditSignedUrl[] {
  if (!isRecord(body) || !Array.isArray(body.urls)) return [];
  const urls: ExpenseAuditSignedUrl[] = [];
  for (const raw of body.urls) {
    if (!isRecord(raw)) continue;
    const documentId = readString(raw, "document_id", "documentId");
    const url = readString(raw, "url", "url");
    const contentType = readString(raw, "content_type", "contentType");
    const expiresAt = readString(raw, "expires_at", "expiresAt");
    if (documentId === null || url === null || contentType === null || expiresAt === null) continue;
    urls.push({ documentId, url, contentType, expiresAt });
  }
  return urls;
}
